import classNames from 'classnames';
import type { HTMLAttributes } from 'react';
import React, { forwardRef } from 'react';
import type { V3To } from '../utils/router/v3Bridge';
import styles from './DropdownMenuItem.module.scss';
import Button from './Button';

export type DropdownMenuItemType<Data> = {
  id: string;
  className?: string;
  link?: V3To;
  data: Data;
};

export type RenderItemFn<Data> = (
  data: Data,
  options: { id: string; focused?: boolean },
) => React.ReactNode;

interface DropdownMenuItemProps<Data>
  extends Pick<HTMLAttributes<HTMLElement>, 'onClick' | 'onMouseEnter'> {
  item: DropdownMenuItemType<Data>;
  focused?: boolean;
  renderItem: RenderItemFn<Data>;
}

const DropdownMenuItemInner = <Data,>(
  {
    item,
    focused,
    onClick,
    onMouseEnter,
    renderItem,
  }: DropdownMenuItemProps<Data>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const inner = renderItem(item.data, { id: item.id, focused });

  return (
    <Button
      ref={ref}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={classNames(styles.item, item.className, {
        [styles.focused]: focused,
      })}
      role="menuitem"
      to={item.link}
    >
      {inner}
    </Button>
  );
};

const DropdownMenuItem = forwardRef(DropdownMenuItemInner) as <Data>(
  props: DropdownMenuItemProps<Data> & {
    ref?: React.ForwardedRef<HTMLButtonElement>;
  },
) => ReturnType<typeof DropdownMenuItemInner>;

export default DropdownMenuItem;
